import { AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { MarkdownContent } from '../../ui/markdown-content';
import { Skeleton } from '../../ui/skeleton';
import { useVfsContent } from '@/hooks/useVfsContent';
import type { AuiRendererProps } from '../registry';

const variantConfig: Record<string, { icon: typeof Info; bg: string; text: string; border: string }> = {
  info:    { icon: Info,           bg: 'bg-status-info-bg',    text: 'text-status-info-fg',    border: 'border-status-info-border'    },
  success: { icon: CheckCircle,    bg: 'bg-status-success-bg', text: 'text-status-success-fg', border: 'border-status-success-border' },
  warning: { icon: AlertTriangle,  bg: 'bg-status-warning-bg', text: 'text-status-warning-fg', border: 'border-status-warning-border' },
  error:   { icon: AlertCircle,    bg: 'bg-status-error-bg',   text: 'text-status-error-fg',   border: 'border-status-error-border'   },
};

export function AuiAlert({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const title = props.title as string | undefined;
  const inlineDescription = (props.description as string) ?? (props.text as string) ?? '';
  const descriptionUrl = props.descriptionUrl as string | undefined;
  const fetched = useVfsContent(descriptionUrl);
  const variant = (props.variant as string) ?? 'info';

  const config = variantConfig[variant] ?? variantConfig.info;
  const Icon = config.icon;

  const description = descriptionUrl ? (fetched.data ?? '') : inlineDescription;

  return (
    <div
      className={`flex gap-3 rounded-lg border p-4 ${config.bg} ${config.border}`}
      data-testid="aui-alert"
    >
      <Icon className={`h-5 w-5 shrink-0 ${config.text}`} />
      <div className="min-w-0 flex-1">
        {title && <p className={`text-sm font-medium ${config.text}`}>{title}</p>}
        <div className={`${config.text} ${title ? 'mt-1' : ''}`}>
          {descriptionUrl && fetched.loading ? (
            <Skeleton className="h-3 w-2/3" />
          ) : descriptionUrl && fetched.error ? (
            <span>Failed to load: {fetched.error.message}</span>
          ) : (
            <MarkdownContent size="xs">{description}</MarkdownContent>
          )}
        </div>
      </div>
    </div>
  );
}
